/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useMemo, useState } from 'react'
import { Content, ContentMain } from './styles'
import { useNavigate } from 'react-router-dom'
import api from '../../services/axios'
import { useMonthFilter } from '../../context/FiltersContext/MonthFilter'
import { useAuth } from '../../context/AuthContext'
import { carregarDistribuidores } from '../../services/dists'
import { intervaloDoMes } from '../../constantes'
import { toast } from 'react-toastify'
import { exportRegularidadeExcel } from '../../view/RegularView/exportRegularidadeExcel'
// REMOVIDO: import { HoverableCell } from '../../hooks/PositionDynamic'

export interface InfoSendProps {
  activeFilter?: string
  onRegisterExport?: (exportFn: () => void) => void
}

function toLocalDate(dateLike: string | number | Date | null | undefined): Date | null {
  if (dateLike == null) return null
  if (dateLike instanceof Date) {
    return new Date(dateLike.getFullYear(), dateLike.getMonth(), dateLike.getDate())
  }
  if (typeof dateLike === 'number') {
    const dt = new Date(dateLike)
    if (isNaN(dt.getTime())) return null
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
  }
  const s = String(dateLike).trim()
  if (!s) return null

  // 1. Formato YYYYMMDD ou DDMMAAAA (8 dígitos sem separador)
  const m8 = s.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (m8) {
    const p1 = Number(m8[1])
    const p2 = Number(m8[2])
    const p3 = Number(m8[3])
    if (p1 >= 1900 && p1 <= 2100 && p2 >= 1 && p2 <= 12 && p3 >= 1 && p3 <= 31) {
      return new Date(p1, p2 - 1, p3)
    }
  }

  const m8br = s.match(/^(\d{2})(\d{2})(\d{4})$/)
  if (m8br) {
    const d = Number(m8br[1])
    const mo = Number(m8br[2])
    const y = Number(m8br[3])
    if (y >= 1900 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return new Date(y, mo - 1, d)
    }
  }

  // 2. Formato YYYY-MM-DD ou YYYY_MM_DD
  const mIso = s.match(/^(\d{4})[-_.](\d{2})[-_.](\d{2})/)
  if (mIso) {
    return new Date(Number(mIso[1]), Number(mIso[2]) - 1, Number(mIso[3]))
  }

  // 3. Formato DD/MM/YYYY ou DD-MM-YYYY
  const mBr = s.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/)
  if (mBr) {
    return new Date(Number(mBr[3]), Number(mBr[2]) - 1, Number(mBr[1]))
  }

  const parsed = new Date(s)
  if (isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

function InfoSend({ activeFilter = 'all', onRegisterExport }: InfoSendProps) {
  const navigate = useNavigate()
  const { month: selectedMonth, year: selectedYear, distributorId } = useMonthFilter()
  const { user } = useAuth()
  const [allFiles, setAllFiles] = useState<any[]>([])
  const [allEstoque, setAllEstoque] = useState<any[]>([])
  const [allEstoqueMes, setAllEstoqueMes] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [allJustify, setAllJustify] = useState<any[]>([])
  const infoRef = useRef<HTMLDivElement | null>(null)
  const [userRole, setUserRole] = useState<string | null>(null)
  const [distribuidores, setDistribuidores] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    const fetchDist = async () => {
      setLoading(true)

      try {
        setUserRole(user?.role ?? null)

        const distList = await carregarDistribuidores()
        setDistribuidores(distList)

        // Três chamadas no lugar de 3×N. As coleções aceitam distIds e
        // recorte por período, então nem o fan-out por distribuidor nem o
        // download do histórico inteiro são mais necessários — os useMemo
        // abaixo já reduziam tudo a um mês só.
        const { from, to } = intervaloDoMes(selectedYear, selectedMonth)
        const params = distributorId
          ? { distIds: String(distributorId), from, to }
          : { from, to }

        const [filesRes, justRes, estRes, estMesRes] = await Promise.all([
          api.get('/files', { params }).catch(() => ({ data: [] })),
          api.get('/j', { params }).catch(() => ({ data: [] })),
          api.get('/get/estoque', { params }).catch(() => ({ data: [] })),
          api.get('/estoque/mensal').catch(() => ({ data: [] })),
        ])

        const lista = (r: any) => {
          const bruto = r?.data?.data ?? r?.data ?? []
          return Array.isArray(bruto) ? bruto : []
        }

        setAllFiles(lista(filesRes))
        setAllJustify(lista(justRes))
        setAllEstoque(lista(estRes))
        setAllEstoqueMes(lista(estMesRes))
      } catch (error) {
        console.error('Erro ao buscar dados:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDist()
    // mês e ano entram nas dependências porque agora vão como from/to na
    // requisição — sem eles, trocar de mês não recarregaria nada.
  }, [distributorId, selectedMonth, selectedYear, navigate])

  const files = useMemo(() => {
    return allFiles.filter((fi) => {
      const fileDate = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
      if (!fileDate) return false

      const matchDate =
        fileDate.getMonth() === selectedMonth && fileDate.getFullYear() === selectedYear

      if (!distributorId) return matchDate

      const distId = String(fi.dist_id ?? fi.distribuidor_id ?? '')
      return matchDate && distId === String(distributorId)
    })
  }, [allFiles, selectedMonth, selectedYear, distributorId])

  const estoqueDb = useMemo(() => {
    return allEstoque.filter((e) => {
      const eDate = toLocalDate(e.dt_ref || e.created_at)
      if (!eDate) return false

      const matchDate =
        eDate.getMonth() === selectedMonth && eDate.getFullYear() === selectedYear

      if (!distributorId) return matchDate

      const distId = String(e.dist_id ?? '')
      return matchDate && distId === String(distributorId)
    })
  }, [allEstoque, selectedMonth, selectedYear, distributorId])

  const estoqueMesFiltered = useMemo(() => {
    return allEstoqueMes.filter((em) => {
      const matchMonth = Number(em.mes) === selectedMonth + 1
      const matchYear = Number(em.ano) === selectedYear
      if (!distributorId) return matchMonth && matchYear
      return matchMonth && matchYear && String(em.dist_id) === String(distributorId)
    })
  }, [allEstoqueMes, selectedMonth, selectedYear, distributorId])

  const justify = useMemo(() => {
    return allJustify.filter((j) => {
      const refDate = toLocalDate(j.dt_referencia)
      if (!refDate) return false

      const matchDate =
        refDate.getMonth() === selectedMonth && refDate.getFullYear() === selectedYear

      if (!distributorId) return matchDate

      const distId = String(j.dist_id ?? j.distribuidor_id ?? '')
      return matchDate && distId === String(distributorId)
    })
  }, [allJustify, selectedMonth, selectedYear, distributorId])

  const filteredDistribuidores = useMemo(() => {
    if (!distributorId) return distribuidores
    return distribuidores.filter((d) => String(d.id) === String(distributorId))
  }, [distribuidores, distributorId])

  useEffect(() => {
    if (onRegisterExport) {
      onRegisterExport(() => {
        if (loading) {
          toast.info('Carregando dados da tela, aguarde um instante...')
          return
        }
        exportRegularidadeExcel({
          distribuidores,
          files,
          estoqueDb,
          estoqueMesFiltered,
          justify,
          selectedMonth,
          selectedYear,
          distributorId,
        })
      })
    }
  }, [
    onRegisterExport,
    loading,
    distribuidores,
    files,
    estoqueDb,
    estoqueMesFiltered,
    justify,
    selectedMonth,
    selectedYear,
    distributorId,
  ])

  const generateDaysInMonth = (year: number, month: number) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    return Array.from({ length: daysInMonth }, (_, i) => i + 1)
  }

  const getDayStatus = (
    hasFile: boolean,
    justification: any,
    isSunday: boolean,
    isPast: boolean,
  ) => {
    if (hasFile) return 'enviado'
    if (justification) return 'justificado'
    if (isSunday) {
      if (isPast) return 'domingo'
      return 'none'
    }
    if (isPast) return 'atraso'
    return 'none'
  }

  const renderIcon = (
    dayNumber: number,
    type: 'estoque' | 'sellout',
    distId: string,
    justificationForDay: any,
    isSunday: boolean,
    hasFile: boolean,
    isPast: boolean,
  ) => {
    if (hasFile) {
      return (
        <span
          style={{
            background: '#4a7733',
            width: 16,
            height: 16,
            borderRadius: 20,
            display: 'block',
          }}
        />
      )
    }
    if (justificationForDay) {
      return (
        <span
          style={{
            background: '#d58938',
            width: 16,
            height: 16,
            borderRadius: 20,
            display: 'block',
          }}
        />
      )
    }
    if (isSunday) {
      if (isPast) {
        return (
          <span
            style={{
              background: '#a7a7a7',
              width: 16,
              height: 16,
              borderRadius: 20,
              display: 'block',
            }}
          />
        )
      }
      return <div style={{ width: 16, height: 16 }} />
    }
    if (isPast) {
      return (
        <span
          style={{
            background: '#b11919',
            width: 16,
            height: 16,
            borderRadius: 20,
            display: 'block',
          }}
        />
      )
    }
    return <div style={{ width: 16, height: 16 }} />
  }

  return (
    <Content>
      {loading ? (
        <div className='loading-login'>
          <p className='btn-text'>Carregando...</p>
        </div>
      ) : (
        <ContentMain>
          <div className='section'>
            {filteredDistribuidores
              .filter((item: any) => {
                const status = item.status !== ' ' ? item.status : '1'
                if (status !== '0') return true

                const hasFilesThisMonth = files.some(
                  (fi) => String(fi.dist_id ?? fi.distribuidor_id ?? '') === String(item.id),
                )
                const hasEstoqueDbThisMonth = estoqueDb.some(
                  (e) => String(e.dist_id) === String(item.id),
                )
                const hasEstoqueMes = estoqueMesFiltered.some(
                  (em) => String(em.dist_id) === String(item.id),
                )
                return hasFilesThisMonth || hasEstoqueDbThisMonth || hasEstoqueMes
              })
              .map((item) => {
                const distEstoqueFiles = files.filter((fi) => {
                  const fileDate = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
                  if (!fileDate) return false
                  const nm = String(fi.nm_file || '').toLowerCase()
                  const isEstoque = nm.includes('estoque') || nm.includes('esto') || nm.startsWith('est')
                  return (
                    String(fi.dist_id ?? fi.distribuidor_id ?? '') === String(item.id) &&
                    isEstoque &&
                    fileDate.getMonth() === selectedMonth &&
                    fileDate.getFullYear() === selectedYear
                  )
                })

                // Registros de estoque diretamente da tabela GID_ESTOQUE
                const distEstoqueDb = estoqueDb.filter((e) => String(e.dist_id) === String(item.id))

                // Dia máximo de estoque via Arquivos
                const maxEstoqueDayFromFiles = distEstoqueFiles.reduce((max, fi) => {
                  const d = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
                  if (!d || d.getMonth() !== selectedMonth || d.getFullYear() !== selectedYear) return max
                  const day = d.getDate()
                  return day > max ? day : max
                }, 0)

                // Dia máximo de estoque via Tabela GID_ESTOQUE
                const maxEstoqueDayFromDb = distEstoqueDb.reduce((max, e) => {
                  const d = toLocalDate(e.dt_ref || e.created_at)
                  if (!d || d.getMonth() !== selectedMonth || d.getFullYear() !== selectedYear) return max
                  const day = d.getDate()
                  return day > max ? day : max
                }, 0)

                const today = new Date()
                const isCurrentMonth =
                  selectedMonth === today.getMonth() && selectedYear === today.getFullYear()
                const isFutureMonth =
                  selectedYear > today.getFullYear() ||
                  (selectedYear === today.getFullYear() && selectedMonth > today.getMonth())

                let maxEstoqueDay = isFutureMonth
                  ? 0
                  : Math.max(maxEstoqueDayFromFiles, maxEstoqueDayFromDb)

                // No mês corrente, o estoque só pode cobrir até no máximo o dia de hoje
                if (isCurrentMonth) {
                  maxEstoqueDay = Math.min(maxEstoqueDay, today.getDate())
                }

                return (
                  <div className='header' key={item.id}>
                    <p style={{ marginBottom: 10, textTransform: 'capitalize', color: '#001842', fontWeight: 700, fontSize: 14 }}>
                      {item.name}
                    </p>
                    <div className='left'>
                      <div className='left-header'>
                        <p className='info-header' style={{ color: '#1e293b', fontWeight: 700 }}>Estoque</p>
                        <p className='info-header' style={{ color: '#1e293b', fontWeight: 700 }}>Sellout</p>
                      </div>
                      <div className='info' ref={infoRef}>
                        {generateDaysInMonth(selectedYear, selectedMonth).map((day) => {
                          const todayDate = new Date()
                          const dayDate = new Date(selectedYear, selectedMonth, day)
                          const isPast =
                            dayDate <
                            new Date(
                              todayDate.getFullYear(),
                              todayDate.getMonth(),
                              todayDate.getDate(),
                            )

                          const Sunday = new Date(selectedYear, selectedMonth, day)
                          const isSunday = Sunday.getDay() === 0

                          const justificationForDay = justify.find((j) => {
                            const refDate = toLocalDate(j.dt_referencia)
                            return (
                              String(j.dist_id) === String(item.id) &&
                              refDate &&
                              refDate.getDate() === day &&
                              refDate.getMonth() === selectedMonth &&
                              refDate.getFullYear() === selectedYear
                            )
                          })

                          // O estoque substitui os dias anteriores: se tem dia 21, cobre de 1 até 21 (mas não após 21 nem após hoje)
                          const isEstoqueCovered = maxEstoqueDay > 0 && day <= maxEstoqueDay

                          const selloutFile = files.find((fi) => {
                            const fileDate = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
                            if (!fileDate) return false
                            const nm = String(fi.nm_file || '').toLowerCase()
                            const isSellout =
                              (nm.includes('sellout') ||
                                nm.includes('vendas') ||
                                nm.includes('venda')) &&
                              !nm.includes('forcavendas') &&
                              !nm.includes('força_vendas') &&
                              !nm.includes('forca_vendas')

                            return (
                              String(fi.dist_id ?? fi.distribuidor_id ?? '') === String(item.id) &&
                              isSellout &&
                              fileDate.getDate() === day &&
                              fileDate.getMonth() === selectedMonth &&
                              fileDate.getFullYear() === selectedYear
                            )
                          })
                          const hasFileSellout = !!selloutFile

                          const estoqueStatus = getDayStatus(
                            isEstoqueCovered,
                            justificationForDay,
                            isSunday,
                            isPast,
                          )
                          const selloutStatus = getDayStatus(
                            hasFileSellout,
                            justificationForDay,
                            isSunday,
                            isPast,
                          )

                          const showEstoque =
                            activeFilter === 'all' || activeFilter === estoqueStatus
                          const showSellout =
                            activeFilter === 'all' || activeFilter === selloutStatus

                          const estoqueIcon = renderIcon(
                            day,
                            'estoque',
                            item.id,
                            justificationForDay,
                            isSunday,
                            isEstoqueCovered,
                            isPast,
                          )
                          const selloutIcon = renderIcon(
                            day,
                            'sellout',
                            item.id,
                            justificationForDay,
                            isSunday,
                            hasFileSellout,
                            isPast,
                          )

                          return (
                            <div className='right' key={day} data-day={day}>
                              {/* Estoque */}
                              <div
                                style={{
                                  transition: 'all 0.3s ease',
                                  opacity: showEstoque ? 1 : 0.15,
                                  filter: showEstoque ? 'none' : 'grayscale(1)',
                                  pointerEvents: showEstoque ? 'auto' : 'none',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {estoqueIcon}
                                </div>
                              </div>

                              {/* Sellout */}
                              <div
                                style={{
                                  transition: 'all 0.3s ease',
                                  opacity: showSellout ? 1 : 0.15,
                                  filter: showSellout ? 'none' : 'grayscale(1)',
                                  pointerEvents: showSellout ? 'auto' : 'none',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {selloutIcon}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>
        </ContentMain>
      )}
    </Content>
  )
}

export default InfoSend
